import { put, select, takeLatest } from 'redux-saga/effects'
import { setComments, saveVitals } from '../store/vitals.actions'
import { VitalsConstants } from '../store/vitals.constants'
import { IScaleOptionDefinition, IScaleSelection, IPainScales, IPatientVitals } from '../store/vitals.types'
import { getPatientVitalsSelector } from '../selectors/patientVitals.selector'
import { getVitalOptionsSelector } from '../../options/selectors'
import { IVitalOptionsState } from '../../options/store/options.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { removeCommentBlock, updateCommentBlock } from '../utils/vitals.util'

const PAIN_PREFIX = 'Pain Scale Scores:'

const upsertPainScaleComment = (comments: string, painBlock: string, shouldRemove: boolean): string => {
  if (shouldRemove || !painBlock) {
    return removeCommentBlock(comments, PAIN_PREFIX)
  }

  return updateCommentBlock(comments, painBlock, PAIN_PREFIX)
}

function formatSectionEntry(
  section: IScaleOptionDefinition['sections'][number],
  selections: IScaleSelection[],
  scaleId: number
): string | null {
  const match = selections.find((s) => s.scaleId === scaleId && s.sectionLabel === section.sectionLabel)
  if (!match?.name) return null
  return `${section.sectionLabel.replace(/\n/g, ' ')}: ${match.name.replace(/\n/g, ' ')}`
}

function buildPainLine(selections: IScaleSelection[], scales: IScaleOptionDefinition[]): string {
  const parts = scales
    .filter((scale) => selections.some((s) => s.scaleId === scale.scaleId))
    .map((scale) => {
      const sections = scale.sections
        ?.map((s) => formatSectionEntry(s, selections, scale.scaleId))
        .filter(Boolean)
        .join(', ')

      return sections ? `${scale.scaleLabel.replace(/\n/g, ' ')} score values are ${sections}` : null
    })
    .filter(Boolean)

  return parts.length ? `${PAIN_PREFIX} ${parts.join(', ')};` : ''
}

export function* setPainScaleComments() {
  const vitals: IPatientVitals = yield select(getPatientVitalsSelector)
  const vitalOptions: IVitalOptionsState = yield select(getVitalOptionsSelector)

  const { comments, painPNNV, painScales } = vitals
  const { painScaleSelections = [] } = (painScales as IPainScales) ?? {}
  const configuredScales: IScaleOptionDefinition[] = Array.isArray(vitalOptions.additionalPainScaleOptions)
    ? (vitalOptions.additionalPainScaleOptions as IScaleOptionDefinition[])
    : []

  const painBlock = buildPainLine(painScaleSelections, configuredScales)

  const updated = upsertPainScaleComment(comments ?? '', painBlock, !!painPNNV)

  if (updated !== (comments ?? '')) {
    yield put(setComments(updated))
  }
  yield put(saveVitals())
}

export function* setPainScaleCommentsSaga() {
  yield takeLatest([VitalsConstants.SET_PAIN_SCALES], performWithMutexHOG, setPainScaleComments)
}
